import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { NumberCard } from "../../../../../components/cards/numberCard";
import { TagCard } from "../../../../../components/cards/tagCard";
import { SearchInput } from "../../../../../components/inputs/searchInput";
import { ConfirmationModal } from "../../../../../components/modals/app_modals/confirmationModal";
import { MakeRepaymentModal } from "../../../../../components/modals/app_modals/loans/makeRepaymentModal";
import { LoanRepaymentScheduleModal } from "../../../../../components/modals/app_modals/loans/repaymentScheduleModal";
import { RequestLoanModal } from "../../../../../components/modals/app_modals/loans/request";
import { UploadLoanRepaymentModal } from "../../../../../components/modals/app_modals/loans/uploadRepaymentModal";
import { TableComponent } from "../../../../../components/table/table";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import {
  formatToCurrency,
  formatToNumber,
  getOffsetFromPage,
  getPageFromOffset,
} from "../../../../../helperFunctions";
import { SingleCooperativeDetails } from "../../../../../redux/features/cooperative/cooperative.type";
import {
  useFetchMemberLoanDetailsQuery,
  useMemberCancelLoanRequestMutation,
} from "../../../../../redux/features/loans/loans.api";
import {
  MemberLoansDetailsQuery,
  SingleLoan,
} from "../../../../../redux/features/loans/loans.type";
import { loanStatusMap } from "../../../../../utils/constants";

export const CooperativeMemberLoansView = ({
  cooperativeDetails,
}: {
  cooperativeDetails: SingleCooperativeDetails;
}) => {
  const [selectedEntry, setSelectedEntry] = useState<SingleLoan>();
  const [query, setQuery] = useState<MemberLoansDetailsQuery>({
    coop: cooperativeDetails.id,
    logs: true,
    text: undefined,
    limit: 10,
    offset: 0,
  });

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const navigate = useNavigate();

  const {
    data: memberLoans,
    isLoading: loadingMemberLoans,
    isFetching: fetchingMemberLoans,
    refetch: refetchMemberLoans,
  } = useFetchMemberLoanDetailsQuery(query, {
    refetchOnMountOrArgChange: true,
  });

  const [cancelLoanRequest, { isLoading: cancellingRequest }] =
    useMemberCancelLoanRequestMutation();

  return (
    <>
      <div className="w-full flex flex-col gap-6">
        <NumberCard
          title={"My Loan Balance"}
          value={memberLoans?.data?.summary}
          primaryButtonText={"Request Loan"}
          onPrimaryButtonClick={() => {
            handleModalOpen("requestLoanModal");
          }}
          primaryButtonVariant="primary-white"
          primaryButtonIcon={"plus"}
          primaryButtonDisabled={false}
          loading={loadingMemberLoans}
        />

        <div className="flex items-center flex-wrap gap-2">
          <h3 className="text-base font-medium flex-1">Loan History</h3>

          <SearchInput
            placeholder="Search"
            searchFunction={(val) => {
              setQuery({ ...query, text: val });
            }}
            className="w-full md:max-w-[286px] !px-4 !py-2"
            iconDimension={16}
          />
        </div>

        <TableComponent
          stickyLastCol
          mobileCardView={false}
          className="w-full !min-w-max md:!min-w-full"
          isRowClickable={true}
          headers={["Loan Type", "Loan Amount", "Loan Tenure", "Status"]}
          headerActionProps={{
            hasMoreMenu: true,
          }}
          isLoading={fetchingMemberLoans}
          isTableEmpty={[0, undefined].includes(
            memberLoans?.data?.Loan?.results?.length
          )}
          currentPage={getPageFromOffset(query.offset ?? 0, query.limit ?? 0)}
          isPaginated
          itemsPerPage={query?.limit}
          onPageChange={(page) => {
            setQuery({
              ...query,
              offset: getOffsetFromPage(page, query.limit ?? 0),
            });
          }}
          emptrySrc="/assets/images/empty_state_cooperatives.svg"
          emptyTitle="No loans found"
          totalCount={memberLoans?.data?.Loan?.count}
          rows={
            memberLoans?.data?.Loan?.results?.map((item, idx) => {
              return {
                id: item?.id,
                items: [
                  item?.LoanProduct?.LoanName,
                  formatToCurrency(item.Amount, {
                    decimalPlaces: 2,
                    currency: "NGN",
                  }),
                  `${formatToNumber(item.Tenure)} months`,
                  <TagCard
                    text={loanStatusMap[item.Status].text}
                    type={loanStatusMap[item.Status].tagVariant}
                  />,
                ],
                onClick: () => {
                  navigate(
                    `/app/my-cooperatives/${cooperativeDetails.id}/loan/${item.id}/member/view`
                  );
                },
                actionProps: {
                  hasActions: true,
                  hasCheckbox: false,
                  hasMoreMenu: true,
                  moreOptions: [
                    {
                      icon: "calendar",
                      text: "View Schedule",
                      onClick: () => {
                        setSelectedEntry(item);
                        handleModalOpen("viewRepaymentScheduleModal");
                      },
                      hidden: false,
                    },
                    {
                      icon: "card",
                      text: "Make Repayment",
                      onClick: () => {
                        setSelectedEntry(item);
                        handleModalOpen("makeRepaymentModal");
                      },
                      hidden: item.Status !== "active",
                    },
                    {
                      icon: "arrow-up",
                      text: "Upload Repayment",
                      onClick: () => {
                        setSelectedEntry(item);
                        handleModalOpen("uploadLoanRepaymentModal");
                      },
                      hidden: item.Status !== "active",
                    },
                    {
                      icon: "circle-close",
                      text: "Cancel Loan Request",
                      onClick: () => {
                        setSelectedEntry(item);
                        handleModalOpen("cancelLoanRequestModal");
                      },
                      hidden:
                        item.Status !== "in-progress" &&
                        item.Status !== "waiting",
                    },
                  ],
                },
              };
            }) ?? []
          }
        />
      </div>

      {selectedEntry ? (
        <>
          <MakeRepaymentModal
            modal={modal}
            modalOpen={modalOpen}
            handleModalClose={() => {
              handleModalClose();
              setSelectedEntry(undefined);
            }}
            handleModalOpen={handleModalOpen}
            loanDetails={selectedEntry}
            onSuccess={async () => {
              await refetchMemberLoans();
            }}
          />

          <LoanRepaymentScheduleModal
            isModalOpen={modalOpen && modal === "viewRepaymentScheduleModal"}
            onModalClose={() => {
              handleModalClose();
              setSelectedEntry(undefined);
            }}
            loanDetails={selectedEntry}
            type="member"
          />

          <UploadLoanRepaymentModal
            modal={modal}
            modalOpen={modalOpen}
            handleModalClose={() => {
              handleModalClose();
              setSelectedEntry(undefined);
            }}
            handleModalOpen={handleModalOpen}
            loanDetails={selectedEntry}
            onSuccess={async () => {}}
          />

          <ConfirmationModal
            isModalOpen={modalOpen && modal === "cancelLoanRequestModal"}
            onModalClose={() => {
              handleModalClose();
              setSelectedEntry(undefined);
            }}
            title={"Cancel Loan Request"}
            children={undefined}
            subtitle={`Are you sure you want to cancel your request for a ${formatToNumber(
              selectedEntry.Tenure
            )} month ${selectedEntry.LoanProduct.LoanName}?`}
            onProceed={async () => {
              const resp = await cancelLoanRequest({
                coop: cooperativeDetails.id,
                loanId: selectedEntry.id,
              });

              if (resp.data?.success) {
                handleModalClose();
                setSelectedEntry(undefined);
                refetchMemberLoans();
              }
            }}
            loading={cancellingRequest}
            proceedButtonText="Yes, Proceed"
          />
        </>
      ) : undefined}

      <RequestLoanModal
        modalOpen={modal === "requestLoanModal" && modalOpen}
        onModalClose={() => {
          handleModalClose();
        }}
        cooperativeDetails={cooperativeDetails}
        onSuccess={() => {
          refetchMemberLoans();
          handleModalClose();
        }}
      />
    </>
  );
};
